// Ingredient waste % helpers. Waste inflates the EFFECTIVE quantity of a raw
// ingredient (a 200 g portion at 10% waste is costed as 220 g), so dish cost and
// cost% rise. Used by every frontend cost-calc site so the math never drifts.
// Server twin: resolveWaste/applyWaste in server/domains/costPrice/dishCostCalc.ts.

export const DEFAULT_WASTE_PERCENTAGE = 0;

/**
 * Resolve the waste % to apply to one ingredient:
 *   - an explicit per-ingredient value (incl. 0) overrides;
 *   - otherwise inherit the restaurant-wide default;
 *   - otherwise 0.
 * Clamped to [0, 100] so a bad stored value can't produce negative/absurd cost.
 */
export function resolveWaste(
  ingredientWaste: number | undefined,
  restaurantDefault: number | undefined,
): number {
  const raw = ingredientWaste ?? restaurantDefault ?? 0;
  if (!Number.isFinite(raw)) return 0;
  return Math.min(100, Math.max(0, raw));
}

/** Inflate a quantity by a waste %. wastePct=0 → factor 1.0 (unchanged). */
export function applyWaste(quantity: number, wastePct: number): number {
  return quantity * (1 + wastePct / 100);
}

/**
 * Resolve a stored restaurant-default waste setting to a usable number.
 * Unlike the cost-target resolver (which rejects `0`), a legit `0%` is valid
 * here, so the check is `>= 0`.
 */
export function resolveWastePercentage(raw?: number | null): number {
  return typeof raw === 'number' && Number.isFinite(raw) && raw >= 0
    ? Math.min(100, raw)
    : DEFAULT_WASTE_PERCENTAGE;
}
